import { use, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { WORKING_DOCUMENT_ID } from '../constants/files'
import { ChatSessionContext } from '../contexts/ChatSessionContext'
import type { Prompt } from '../types'
import { DEFAULT_MODEL } from '../types'
import { useDocumentName } from './useDocumentName'
import { useSelectedContext } from './useSelectedContext'

export function useApplyPrompt({
  fetchDocumentName = false,
}: { fetchDocumentName?: boolean } = {}) {
  const { t } = useTranslation()
  const chatSession = use(ChatSessionContext)
  const { updateFileSelection } = useSelectedContext()
  // Only fetch the document name when the caller knows a full-document prompt may be applied.
  const { data: workingDocumentName } = useDocumentName(fetchDocumentName)

  const applyPrompt = useCallback(
    (prompt: Prompt) => {
      const {
        userPrompt,
        llmModel,
        systemPrompt,
        reviewMode,
        requiresDocumentComments,
        requiresFullDocument,
      } = prompt

      chatSession?.setDraftMessage(userPrompt)
      chatSession?.setSelectedModel(llmModel ?? DEFAULT_MODEL)
      chatSession?.setSystemPrompt(systemPrompt ?? '')
      chatSession?.setReviewMode(reviewMode ?? null)
      chatSession?.setIsCommentMode(!!reviewMode)
      chatSession?.setIncludeComments(requiresDocumentComments)
      chatSession?.setRequiresFullDocument(requiresFullDocument)

      updateFileSelection({
        id: WORKING_DOCUMENT_ID,
        fileName: workingDocumentName ?? t('files.untitledDocument'),
        extension: 'docx',
        sections: requiresFullDocument ? [] : null,
      })
    },
    [chatSession, updateFileSelection, workingDocumentName, t]
  )

  return { applyPrompt }
}
